// src/pages/LitCharacterSection.jsx
//
// Student-facing render of a CHARACTER lit_section (sectionKind 'character'). Faithful port
// of the prototype LitCharacterView (between-the-lines-v2.html L12540-12682) PLUS the SVG
// arc chart LitCharacterArcChart (L12282-12372), inlined here as CharacterArcChart.
//
// FLATTEN MISMATCH (the big one this slice -- mirrors LitCharacterEditor's mapping):
// the prototype kept a nested object; our hybrid model stores 9 FLAT keys under
// content.blocks (CHARACTER_BLOCKS, litSectionShape.js):
//   analysis.intro    -> blocks.analysisIntro    arc (text)  -> blocks.arcText
//   analysis.facets   -> blocks.facets           arc.yLabel  -> blocks.arcYLabel
//   analysis.keyIdeas -> blocks.keyIdeas         arc.points  -> blocks.arcPoints
//   quotes -> blocks.quotes   relationships -> blocks.relationships   essays -> blocks.essays
// The arc chart's `data` = { yLabel: blocks.arcYLabel, points: blocks.arcPoints }.
//
// Pure presentational (takes `content`). Deferred like the Overview port: glossary
// highlighting (renderLitText -> plain text), Export PDF, podcast player, the quiz tab, and
// the quotes-tab "jump to scene" link (act ref shown as a static label, not a button).
import React, { Fragment, useState } from 'react';
import { IconLock } from '../components/icons.jsx';
import AnnotatedText from '../components/AnnotatedText.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

const TABS = [
    ['analysis', 'Analysis'],
    ['arc', 'Character Arc'],
    ['quotes', 'Key Quotes'],
    ['relationships', 'Relationships'],
    ['essays', 'Essay Frames'],
];

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

// SVG curve of trajectory across the play. Faithful port of the prototype
// LitCharacterArcChart; relies on the .character-arc-chart / .arc-curve / .arc-node CSS
// in src/index.css (incl. day-mode overrides), so it inherits day/night theming.
function CharacterArcChart({ data, label }) {
    const [hover, setHover] = useState(null);
    if (!data || !data.points || data.points.length === 0) return null;
    const W = 800, H = 320;
    const padL = 80, padR = 30, padT = 30, padB = 70;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const n = data.points.length;
    const yVal = (p) => { const v = Number(p && p.y); return Number.isFinite(v) ? v : 50; };
    const xFor = (i) => n === 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW;
    const yFor = (val) => padT + (1 - val / 100) * plotH;
    let path = '';
    for (let i = 0; i < n; i++) {
        const x = xFor(i);
        const y = yFor(yVal(data.points[i]));
        if (i === 0) path += `M ${x} ${y}`;
        else {
            const prevX = xFor(i - 1);
            const prevY = yFor(yVal(data.points[i - 1]));
            const dx = x - prevX;
            path += ` C ${(prevX + dx * 0.45).toFixed(1)} ${prevY.toFixed(1)}, ${(x - dx * 0.45).toFixed(1)} ${y.toFixed(1)}, ${x.toFixed(1)} ${y.toFixed(1)}`;
        }
    }
    const areaPath = path + ` L ${xFor(n - 1).toFixed(1)} ${(H - padB).toFixed(1)} L ${xFor(0).toFixed(1)} ${(H - padB).toFixed(1)} Z`;
    const yTicks = [{ val: 25, label: 'LOW' }, { val: 50, label: 'MID' }, { val: 75, label: 'HIGH' }];
    return (
        <div className="character-arc-chart relative w-full text-book-muted">
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-auto block" style={{ maxHeight: 360 }}>
                <defs>
                    <filter id="arcGlow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="3.5" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <linearGradient id="arcAreaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {yTicks.map((t, i) => (
                    <g key={i}>
                        <line x1={padL} y1={yFor(t.val)} x2={W - padR} y2={yFor(t.val)} stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 5" opacity="0.22" />
                        <text x={padL - 14} y={yFor(t.val) + 4} fill="currentColor" fontSize="11" textAnchor="end" opacity="0.55" fontFamily="Cinzel, serif" letterSpacing="2">{t.label}</text>
                    </g>
                ))}
                <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="currentColor" strokeWidth="0.8" opacity="0.35" />
                <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="currentColor" strokeWidth="0.8" opacity="0.35" />
                {data.points.map((p, i) => {
                    const parts = String((p && p.x) || '').split(' · ');
                    const cx = xFor(i);
                    return (
                        <g key={i}>
                            <line x1={cx} y1={H - padB} x2={cx} y2={H - padB + 6} stroke="currentColor" strokeWidth="0.6" opacity="0.4" />
                            <text x={cx} y={H - padB + 22} fill="currentColor" fontSize="10.5" textAnchor="middle" opacity="0.75" fontFamily="Cinzel, serif" letterSpacing="2">{(parts[0] || '').toUpperCase()}</text>
                            {parts[1] && <text x={cx} y={H - padB + 38} fill="currentColor" fontSize="9.5" textAnchor="middle" opacity="0.55" fontFamily="Cinzel, serif" letterSpacing="1.5">{parts[1].toUpperCase()}</text>}
                        </g>
                    );
                })}
                <path d={areaPath} fill="url(#arcAreaGrad)" />
                <path className="arc-curve" d={path} fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" filter="url(#arcGlow)" />
                {data.points.map((p, i) => {
                    const cx = xFor(i);
                    const cy = yFor(yVal(p));
                    const isHover = hover === i;
                    return (
                        <g key={i} style={{ cursor: 'pointer' }} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onTouchStart={() => setHover((h) => h === i ? null : i)}>
                            <circle cx={cx} cy={cy} r="18" fill="rgba(0,0,0,0)" />
                            <circle className="arc-node" cx={cx} cy={cy} r={isHover ? 9 : 6.5} strokeWidth="2.5" filter="url(#arcGlow)" />
                            {isHover && <text x={cx} y={cy - 16} fill="currentColor" fontSize="11" textAnchor="middle" opacity="0.95" fontFamily="Cinzel, serif" letterSpacing="1.5">{yVal(p)}</text>}
                        </g>
                    );
                })}
            </svg>
            <div className="mt-4 border border-book-accent/40 bg-book-pageDark/50 p-4 min-h-[88px]">
                {hover !== null ? (
                    <div>
                        <div className="font-display text-book-accent text-xs mb-1.5 flex items-center gap-3 flex-wrap font-semibold">
                            <span>{data.points[hover].x}</span>
                            {data.points[hover].act && <span className="text-book-muted">· {data.points[hover].act}</span>}
                            <span className="ml-auto">{yVal(data.points[hover])}%</span>
                        </div>
                        <p className="font-serif text-book-text text-base leading-relaxed">{data.points[hover].event}</p>
                    </div>
                ) : (
                    <p className="font-serif italic text-book-muted text-sm leading-relaxed">Hover any node to read the key event at that station of the play.</p>
                )}
            </div>
            {data.yLabel && (
                <div className="font-display text-book-muted text-xs mt-3 text-center opacity-80 font-semibold">
                    Vertical axis: {data.yLabel}
                </div>
            )}
        </div>
    );
}

export default function LitCharacterSection({ content, done = false }) {
    const blocks = (content && content.blocks) || {};
    const title = (content && content.title) || 'Character';
    const analysisIntro = typeof blocks.analysisIntro === 'string' ? blocks.analysisIntro : '';
    const facets = Array.isArray(blocks.facets) ? blocks.facets : [];
    const keyIdeas = Array.isArray(blocks.keyIdeas) ? blocks.keyIdeas : [];
    const arcText = Array.isArray(blocks.arcText) ? blocks.arcText : [];
    const arcPoints = Array.isArray(blocks.arcPoints) ? blocks.arcPoints : [];
    const quotes = Array.isArray(blocks.quotes) ? blocks.quotes : [];
    const relationships = Array.isArray(blocks.relationships) ? blocks.relationships : [];
    const essays = Array.isArray(blocks.essays) ? blocks.essays : [];
    const arcData = arcPoints.length > 0 ? { yLabel: blocks.arcYLabel, points: arcPoints } : null;
    const [tab, setTab] = useState('analysis');

    return (
        <div className="max-w-3xl mx-auto pb-8">
            <LessonHeader eyebrow="Character · {title" title={title}
                blocks={blocks} done={done} />

            <div className="inline-flex border border-book-accent/40 mb-6 rounded-sm overflow-hidden flex-wrap">
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`px-4 py-2 font-display text-xs transition-colors font-semibold ${tab === k ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === 'analysis' && (
                (analysisIntro || facets.length > 0 || keyIdeas.length > 0) ? (
                    <div className="space-y-5">
                        {analysisIntro && <p className="font-serif text-book-text text-base leading-relaxed whitespace-pre-wrap"><AnnotatedText>{analysisIntro}</AnnotatedText></p>}
                        {facets.length > 0 && (
                            <div className="grid md:grid-cols-2 gap-3">
                                {facets.map((f, i) => (
                                    <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                                        <div className="font-display text-book-muted text-xs mb-1 font-semibold">{f && f.label}</div>
                                        <div className="font-serif text-book-text">{f && f.value}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {keyIdeas.length > 0 && (
                            <div className="space-y-3 pt-2">
                                <div className="font-display text-book-accent text-xs font-semibold">Key ideas</div>
                                {keyIdeas.map((k, i) => (
                                    <div key={i} className="border-l-4 border-book-accent bg-book-pageDark/30 p-4">
                                        <p className="font-serif italic text-book-text leading-relaxed whitespace-pre-wrap">{k}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'arc' && (
                (arcData || arcText.length > 0) ? (
                    <div>
                        <p className="font-serif italic text-book-muted mb-6">The trajectory of {title} across the play.</p>
                        {arcData ? <CharacterArcChart data={arcData} label={title} /> : (
                            <div className="border border-book-accent/30 bg-book-pageDark/40 p-8 text-center">
                                <IconLock className="w-8 h-8 mx-auto mb-3 text-book-accent/50" />
                                <div className="font-display text-book-accent text-sm mb-1 font-semibold">Arc chart coming soon</div>
                                <p className="font-serif italic text-book-muted text-sm">Visual trajectory for {title} is being prepared.</p>
                            </div>
                        )}
                        {arcText.length > 0 && (
                            <Fragment>
                                <div className="my-8 swp-divider text-book-accent/40">
                                    <span className="font-display text-xs font-semibold text-book-muted">Stations of the tragedy</span>
                                </div>
                                <div className="space-y-3">
                                    {arcText.map((a, i) => (
                                        <div key={i} className="flex items-stretch gap-4">
                                            <div className="w-10 h-10 rounded-full border-2 border-book-accent bg-book-pageDark flex items-center justify-center flex-shrink-0">
                                                <span className="font-display text-book-accent text-sm">{i + 1}</span>
                                            </div>
                                            <div className="flex-grow rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                                                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
                                                    <div className="font-display text-book-accent text-sm font-semibold">{a && a.stage}</div>
                                                    <div className="font-display text-book-muted text-xs font-semibold">{a && a.act}</div>
                                                </div>
                                                <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap">{a && a.detail}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Fragment>
                        )}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'quotes' && (
                quotes.length > 0 ? (
                    <div className="space-y-3">
                        {quotes.map((q, i) => (
                            <div key={i} className="border-l-4 border-book-accent bg-book-pageDark/40 p-5">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                    <span className="font-display text-book-accent text-xs font-semibold">{q && q.act}</span>
                                </div>
                                <p className="font-serif italic text-book-text text-base leading-relaxed mb-2">"{q && q.quote}"</p>
                                <p className="font-serif text-book-text/80 text-sm leading-relaxed">→ <AnnotatedText>{q && q.analysis}</AnnotatedText></p>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'relationships' && (
                relationships.length > 0 ? (
                    <div className="space-y-3">
                        <p className="font-serif italic text-book-muted mb-2">How {title} relates to each major character.</p>
                        {relationships.map((r, i) => (
                            <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                                    <div className="font-display text-book-accent text-base">{r && r.name}</div>
                                    <div className="font-serif italic text-book-muted text-xs">{r && r.arc}</div>
                                </div>
                                <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap"><AnnotatedText>{r && r.note}</AnnotatedText></p>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'essays' && (
                essays.length > 0 ? (
                    <div className="space-y-5">
                        <p className="font-serif italic text-book-muted">Frames for the most common exam questions on {title}.</p>
                        {essays.map((e, i) => (
                            <div key={i} className="border border-book-accent/30 bg-book-pageDark/40 p-5">
                                <div className="font-display text-book-accent text-base mb-3 leading-tight">{e && e.q}</div>
                                <ol className="space-y-2">
                                    {(Array.isArray(e && e.frame) ? e.frame : []).map((p, j) => (
                                        <li key={j} className="font-serif text-book-text/90 leading-relaxed pl-4 border-l-2 border-book-accent/40">{p}</li>
                                    ))}
                                </ol>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}
        </div>
    );
}
